import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, loading, globalRoles, hasGlobalRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
      return;
    }

    if (!loading && user && globalRoles.length === 0) {
      // User is authenticated but has no global roles
      // Check if user has any companies, if not redirect to no-access
      navigate('/no-access', { replace: true });
      return;
    }

    if (!loading && user && globalRoles.length > 0) {
      // Check if user has any admin role
      const hasAdminAccess = hasGlobalRole('portal_admin') || 
                            hasGlobalRole('license_manager') || 
                            hasGlobalRole('auditor');
      
      if (!hasAdminAccess) {
        navigate('/dashboard', { replace: true });
        return;
      }
    }
  }, [user, loading, globalRoles, hasGlobalRole, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // Don't render children if user is not authenticated or doesn't have admin access
  if (!user || globalRoles.length === 0) {
    return null;
  }

  const hasAdminAccess = hasGlobalRole('portal_admin') || 
                        hasGlobalRole('license_manager') || 
                        hasGlobalRole('auditor');

  if (!hasAdminAccess) {
    return null;
  }

  return <>{children}</>;
}